import { useEffect, useState } from 'react';
import { classnames } from 'helpers/utils';
import { refreshPage } from 'helpers/refresh-page';
import { LocalStorageApi } from 'helpers/local-storage';
import { AlertTriangleIcon } from 'assets/icons';
import { versionCheckInterval } from 'config/constants';
import styles from './refresh-page-button.module.scss';

const updateVersion = (updatedVersion: string) =>
  LocalStorageApi.set('version', updatedVersion);

const getCurrentRunningVersion = () => LocalStorageApi.get('version');

const fetchLastVersion = async (): Promise<string> =>
  fetch('/version.json')
    .then((response) => response.json())
    .then((data) => data.version)
    .catch(() => null);

//  INFO: this variable is used to check if the component is being rendered for the first time,
//  in order to update the current version only the first ever time this component is rendered.
let isFirstRender = true;

export const RefreshPageButton = () => {
  const [showButton, setShowButton] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    if (versionCheckInterval <= 0) return undefined;

    const checkVersion = async () => {
      const currentVersion = getCurrentRunningVersion();
      const lastFrontendVersion = await fetchLastVersion();

      if (isFirstRender) {
        isFirstRender = false;
        if (
          lastFrontendVersion &&
          (!currentVersion || currentVersion !== lastFrontendVersion)
        ) {
          updateVersion(lastFrontendVersion);
        }
      } else if (currentVersion !== lastFrontendVersion) {
        setLatestVersion(lastFrontendVersion);
        setShowButton(true);
      }
    };

    checkVersion();

    const intervalId = setInterval(checkVersion, versionCheckInterval);
    return () => clearInterval(intervalId);
  }, []);

  const handleClick = () => {
    if (latestVersion) {
      updateVersion(latestVersion);
      refreshPage();
    }
  };

  if (versionCheckInterval <= 0 || !showButton || !latestVersion) return null;
  return (
    <div
      className={classnames(
        styles.container,
        'text__body__regular__small__gray700',
      )}
    >
      <AlertTriangleIcon />
      <div>
        Your app version is outdated.{' '}
        <button
          type="button"
          className={classnames(
            styles.refreshLabel,
            'text__body__regular__small__gray700',
          )}
          onClick={handleClick}
        >
          Click here to refresh
        </button>{' '}
        and update to the latest version.
      </div>
    </div>
  );
};
